import * as React from 'react';
import {weakMemo} from '@packages/utilities/memo';
import {
  TypeImplementation,
  Expressions,
  withSystem,
  Types,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {LogicLabel} from '@packages/systems/flows/shared';
import {
  ExpressionEditor,
  updateExpressionAtAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';

import {
  EList,
  TList,
  TUnion,
  TTypeConstructor,
  TRecord,
  EUnion,
  EHole,
  ERecord,
} from '@packages/systems/wfdl-ast';
import {StyledHelpIcon} from '@packages/systems/plugins/Effect';
import {
  EnhancedDropdownWithNote,
  EnhancedDropdownWithNoteDataProps,
} from '@packages/systems/design-system/Dropdown';
import SelectButton from '@packages/systems/design-system/SelectButton';
import {
  styledDiv,
  styled,
  designerTheme,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {Subheading} from '@packages/systems/design-system/Text';
import {Note} from '@packages/systems/spring';
import TooltipControl from '@packages/systems/design-system/TooltipControl';
import {CheckMedium as Checkmark} from '@packages/systems/ui/icons';
import {qualify, pluginName, typeName} from '@packages/systems/wfdl-Qualified';
import {usePlugins} from '@packages/utilities/platform';

/**
 * WFDL types
 */
const typeOfUpdateUser = withSystem(
  {
    defaultValue: 'Select a user',
    isFullWidth: true,
    bindUIVariant: 'custom',
  },
  TRecord({
    'User Identifier': TTypeConstructor(
      qualify(pluginName('UsersPlugin'), typeName('UserIdentifierDropdownType'))
    ),
    'Selected Fields': TList(
      TUnion({
        acceptCommunications: TTypeConstructor(
          qualify(Plugins.name('Boolean'), typeName('Checkbox'))
        ),
        accessGroups: TTypeConstructor(
          qualify(pluginName('UsersPlugin'), typeName('AccessGroupsAliasType'))
        ),
      })
    ),
  })
);

/**
 * Expression path parts
 */
const atUserIdentifier = {in: 'Record', at: 'User Identifier'} as const;
const atBasicFields = {in: 'Record', at: 'Basic Info Fields'} as const;
const atAccessGroups = {in: 'Record', at: 'accessGroups'} as const;
const atMarketingCommunications = {
  in: 'Record',
  at: 'acceptCommunications',
} as const;
const atSelectedFields = {in: 'Record', at: 'Selected Fields'} as const;

/**
 * Constants
 */
const ITEM_DIVIDER_LABEL_HEIGHT = 12;
const ENHANCED_DROPDOWN_WIDTH = 220;
const noteText =
  'Add a new value for each field you want to update. Any field left blank will be empty.';
const basicFields: Array<{
  id: string;
  path: ExpressionPath;
}> = [
  {
    id: 'acceptCommunications',
    path: [atBasicFields, atMarketingCommunications],
  },
  {
    id: 'accessGroups',
    path: [atBasicFields, atAccessGroups],
  },
];

/**
 * Dispatchers
 */
const dispatchUserIdentifier = weakMemo(
  (dispatch: TypeImplementation.ExpressionEditorDispatch) =>
    extendExpressionEditorActionAtPath(dispatch)([atUserIdentifier])
);
const dispatchToSelectedField = ({
  listIndex,
  tag,
}: {
  listIndex: number;
  tag: string;
}) => {
  return (dispatch: TypeImplementation.ExpressionEditorDispatch) =>
    extendExpressionEditorActionAtPath(dispatch)([
      atSelectedFields,
      {in: 'List', index: listIndex},
      {in: 'Union', tag},
    ]);
};

/**
 * Utils
 * */
// @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
const initializeField = (id) =>
  id === 'acceptCommunications'
    ? EUnion({tag: 'False', value: ERecord({})})
    : id === 'accessGroups'
      ? EList([])
      : EHole();
const getLabelForFieldId = (id: string) =>
  id === 'acceptCommunications'
    ? 'Marketing communications'
    : id === 'accessGroups'
      ? 'Access groups'
      : '';

/**
 * Type that updates a User
 */
export const UpdateUser = TypeImplementation.Alias({
  system: {bindUIVariant: 'custom'},
  type: typeOfUpdateUser,
  render: function RenderUpdateUser({expr, dispatch}) {
    const plugins = usePlugins();
    const record = ContextualExpressions.getRecord(stripAnn(expr));

    const selectedFieldsExpr = record && record['Selected Fields'];
    const selectedList = React.useMemo(
      () =>
        selectedFieldsExpr
          ? Expressions.getList(stripAll(selectedFieldsExpr)) || []
          : [],
      [selectedFieldsExpr]
    );

    // @ts-expect-error - TS2345 - Argument of type '() => (string | null)[]' is not assignable to parameter of type '() => string[]'.
    const selectedFields = React.useMemo<Array<string>>(() => {
      const arr = selectedList

        .map((f) => Expressions.getUnion(f)?.tag || null)
        .filter(Boolean);
      return arr;
    }, [selectedList]);

    const getTypeForFieldId = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'fieldId' implicitly has an 'any' type.
      (fieldId) => {
        if (!selectedFieldsExpr) return;

        const selectedFieldsType = getAnn(selectedFieldsExpr).type;
        const listType = selectedFieldsType
          ? Types.getListItemType(selectedFieldsType)
          : null;

        const unionTypes = listType ? Types.getUnionValues(listType) : null;
        return unionTypes ? unionTypes[fieldId] : null;
      },
      [selectedFieldsExpr]
    );

    /**
     * Get labels for the select menu
     */
    const getLabel = React.useCallback(
      // @ts-expect-error - TS7031 - Binding element 'fieldSlug' implicitly has an 'any' type.
      ({fieldSlug}) => {
        const annotationType = getTypeForFieldId(fieldSlug);
        if (!annotationType) {
          return null;
        }

        const label = getLabelForFieldId(fieldSlug);
        const icon = Types.getIcon(annotationType, plugins);
        const isSelected = selectedFields.includes(fieldSlug);

        return (
          <StyledOption>
            {isSelected ? <StyledCheckmark /> : <Space />}{' '}
            {icon && <Plugins.PluginIcon icon={icon} />}
            {label}
          </StyledOption>
        );
      },
      [selectedFields, plugins, getTypeForFieldId]
    );

    const allFieldsSelected = basicFields.length === selectedFields.length;

    const dropdownList = React.useMemo(() => {
      const basicHeader: EnhancedDropdownWithNoteDataProps[] = [
        {
          id: '_header-basic-info',
          label: getFieldHeader('Basic info'),
          header: true,
          disabled: true,
        },
      ];

      const basicFieldOptions = basicFields.map((field) => {
        return {
          id: field.id,
          label: getLabel({
            fieldSlug: field.id,
            // @ts-expect-error - TS2345 - Argument of type '{ fieldSlug: string; path: ExpressionPath; }' is not assignable to parameter of type '{ fieldSlug: any; }'.
            path: field.path,
          }),
        };
      });

      const toggleSelect = [
        {
          id: `_button-toggle-${
            allFieldsSelected ? 'deselect-all' : 'select-all'
          }`,
          label: allFieldsSelected
            ? 'Deselect all fields'
            : 'Select all fields',
        },
      ];

      const dropdownItems: EnhancedDropdownWithNoteDataProps[] = basicHeader
        .concat(basicFieldOptions)
        // Hacky way of adding a divider
        // above the toggle, but it works
        .concat({
          id: 'divider',
          label: <ItemDivider />,
          disabled: true,
          height: ITEM_DIVIDER_LABEL_HEIGHT,
          style: {
            paddingLeft: designerTheme.gutters.x0,
            paddingRight: designerTheme.gutters.x0,
            paddingTop: designerTheme.gutters.x8,
          },
        })
        .concat(toggleSelect);
      return dropdownItems;
    }, [getLabel, allFieldsSelected]);

    const handleFieldSelect = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
      (id) => {
        // If the user clicks the toggle select all button
        // Select all fields
        if (id === '_button-toggle-select-all') {
          const selectedAllFields = basicFields.map((field) =>
            EUnion({
              tag: field.id,
              value: initializeField(field.id),
            })
          );
          dispatch(
            updateExpressionAtAction(EList(selectedAllFields), [
              atSelectedFields,
            ])
          );
        }

        // Deselect all fields
        else if (id === '_button-toggle-deselect-all') {
          dispatch(updateExpressionAtAction(EList([]), [atSelectedFields]));
        } else {
          // If a field is selected, add or remove it from the Selected Fields expression
          const fieldInList = selectedFields.includes(id);
          if (fieldInList) {
            const newList = selectedList.filter((item) => {
              const union = Expressions.getUnion(item);
              return union?.tag !== id;
            });
            dispatch(
              updateExpressionAtAction(EList(newList), [atSelectedFields])
            );
          } else {
            const exprList = EList([
              ...selectedList,
              EUnion({
                tag: id,
                value: initializeField(id),
              }),
            ]);
            dispatch(updateExpressionAtAction(exprList, [atSelectedFields]));
          }
        }
      },
      [selectedFields, selectedList, dispatch]
    );

    if (!record) {
      return null;
    }

    const selectedListAnn: Array<ContextualExpression> = selectedFieldsExpr
      ? ContextualExpressions.getList(stripAnn(selectedFieldsExpr)) || []
      : [];

    return (
      <GridWrapper>
        <ExpressionEditor
          dispatch={dispatchUserIdentifier(dispatch)}
          // @ts-expect-error - TS2322 - Type 'ContextualExpression | undefined' is not assignable to type 'ContextualExpression'.
          expression={record['User Identifier']}
        />
        <Divider />
        <StyledLabel label="Fields to update" required>
          <StyledTooltipControl
            icon={<StyledHelpIcon />}
            aria-hidden
            content={
              <TooltipContent>
                Select the fields you want to update. Any unselected fields will
                remain unchanged.
              </TooltipContent>
            }
            alternate
          />
        </StyledLabel>

        <EnhancedDropdownWithNote
          button={
            <SelectButton indicator data-automation-id="select-field">
              <>
                <StyledPrimary>
                  {selectedFields.length > 0 ? 'Selected' : 'Select'}{' '}
                  fields{' '}
                </StyledPrimary>
                {selectedFields.length > 0 && (
                  <StyledSecondary>
                    {`(${
                      allFieldsSelected ? 'all' : selectedFields.length
                    } selected)`}
                  </StyledSecondary>
                )}
              </>
            </SelectButton>
          }
          width={ENHANCED_DROPDOWN_WIDTH}
          dropdownData={dropdownList}
          handleChange={handleFieldSelect}
        />

        {selectedFields.length > 0 && (
          <>
            <Note colorVariant="default" hasIcon={false}>
              {noteText}
            </Note>
            <Divider />
          </>
        )}

        {/** Section for rendering the selected basic fields */}
        {selectedListAnn.length > 0 && <Subheading>Basic info</Subheading>}
        {selectedListAnn.map((basicField, index) => {
          const {tag: basicFieldTag, value} =
            ContextualExpressions.getUnion(stripAnn(basicField)) || {};
          if (!basicFieldTag || !value) return;

          // The access groups type renders its own label
          if (basicFieldTag === 'accessGroups') {
            return (
              <ExpressionEditor
                key={basicFieldTag}
                expression={value}
                dispatch={dispatchToSelectedField({
                  listIndex: index,
                  tag: basicFieldTag,
                })(dispatch)}
              />
            );
          }

          // The marketing communicatiions type needs a different
          // alignment and different label then the label in the expression
          if (basicFieldTag === 'acceptCommunications') {
            return (
              <StyledCheckboxWrapper key={basicFieldTag}>
                <div id={basicFieldTag}>
                  <ExpressionEditor
                    key={basicFieldTag}
                    expression={value}
                    dispatch={dispatchToSelectedField({
                      listIndex: index,
                      tag: basicFieldTag,
                    })(dispatch)}
                  />
                </div>
                <RaisedWrapper>
                  <LogicLabel
                    label="The user has agreed to receive marketing or other communications"
                    htmlFor={basicFieldTag}
                    customLabelStyles={{lineHeight: 1.5}}
                  />
                </RaisedWrapper>
              </StyledCheckboxWrapper>
            );
          }
          return (
            <LogicLabel key={basicFieldTag} label={basicFieldTag}>
              <ExpressionEditor
                expression={value}
                dispatch={dispatchToSelectedField({
                  listIndex: index,
                  tag: basicFieldTag,
                })(dispatch)}
              />
            </LogicLabel>
          );
        })}
      </GridWrapper>
    );
  },
});

const GridWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: tokens.space[2],
});

const Divider = styledDiv(({theme: {gutters, colors}}) => ({
  height: 1,
  backgroundColor: colors.backgroundDeep,
  width: `calc(100% + ${gutters.x8 * 2 + gutters.x4}px)`,
  marginLeft: -gutters.x8 - gutters.x4 / 2,
}));

const StyledLabel = styled(LogicLabel)(({theme: {gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: `max-content ${gutters.x16}px`,
  gridColumnGap: gutters.x4,
  alignItems: 'start',

  overflowY: 'visible',
  maxHeight: `${gutters.x16}px`,
  marginBottom: gutters.x4,
}));

const StyledTooltipControl = styled(TooltipControl)(({theme: {gutters}}) => ({
  marginLeft: gutters.x4,
  justifySelf: 'center',
  marginTop: '-2px',
}));

const TooltipContent = styledDiv(({theme: {gutters}}) => ({
  maxWidth: gutters.x4 * 56, // 224
}));

const StyledOption = emotionStyled('div')({
  display: 'flex',
  alignItems: 'center',
  columnGap: tokens.space[1],
});

const Space = emotionStyled('div')({
  width: tokens.space[5],
});

const StyledCheckmark = styled(Checkmark)(({theme: {gutters}}) => ({
  width: gutters.x20,
}));

const StyledFieldHeader = styledDiv(({theme: {gutters}}) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: gutters.x4,
  position: 'relative',
  top: -2,
}));

const getFieldHeader = (label: string) => (
  <StyledFieldHeader>{label}</StyledFieldHeader>
);
const StyledPrimary = emotionStyled('span')({
  color: tokens.colors.textPrimary,
});
const StyledSecondary = emotionStyled('span')({
  color: tokens.colors.textSecondary,
});

const ItemDivider = styled(Divider)(({theme}) => ({
  background: theme.colors.border,
}));
const StyledCheckboxWrapper = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'top',
  columnGap: tokens.space[2],
  marginTop: tokens.space[1],
});
// We need this wrapper to align the checkbox
// and the label for the checkbox, since the line-height pushes it downward
const RaisedWrapper = emotionStyled('div')({
  marginTop: `-2px`,
});
